import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { graphql } from '@/gql'
import { applyTheme } from '@/features/theme/theme'
import type { InstanceLimitsInput, RegistrationPolicy, ThemePreference } from '@/gql/graphql'
import { gqlClient } from '@/lib/graphql'
import { queryClient } from '@/lib/queryClient'

/**
 * Everything the auth feature knows about server state: the documents, the
 * query keys, the options factories, the mutations and their cache effects.
 * Components import a hook and nothing else.
 */

export const authKeys = {
  all: ['auth'] as const,
  users: (query: string) => ['auth', 'users', query] as const,
  me: () => [...authKeys.all, 'me'] as const,
  instance: () => [...authKeys.all, 'instance'] as const,
  instanceLimits: () => [...authKeys.all, 'instance', 'limits'] as const,
  invitePreview: (code: string) => [...authKeys.all, 'invite', code] as const,
}

const MeQuery = graphql(`
  query Me {
    me {
      id
      handle
      displayName
      isInstanceAdmin
      avatarUrl
      themePreference
      createdAt
    }
  }
`)

const SetThemePreferenceMutation = graphql(`
  mutation SetThemePreference($preference: ThemePreference!) {
    setThemePreference(preference: $preference) {
      id
      themePreference
    }
  }
`)

const UsersQuery = graphql(`
  query Users($query: String) {
    users(query: $query) {
      id
      handle
      displayName
      avatarUrl
      isInstanceAdmin
      disabledAt
    }
  }
`)

const SetUserDisabledMutation = graphql(`
  mutation SetUserDisabled($userId: UUID!, $disabled: Boolean!) {
    setUserDisabled(userId: $userId, disabled: $disabled) {
      id
      disabledAt
    }
  }
`)

const InstanceQuery = graphql(`
  query Instance {
    instance {
      registrationPolicy
      needsSetup
      voiceParticipantLimit
      videoPublisherLimit
      attachmentMaxBytes
      attachmentMaxPerMessage
      avatarMaxBytes
      cameraMaxHeight
      cameraMaxFps
      cameraMaxKbps
      screenMaxHeight
      screenMaxFps
      screenMaxKbps
      voiceAudioKbps
    }
  }
`)

const InvitePreviewQuery = graphql(`
  query InvitePreview($code: String!) {
    invitePreview(code: $code) {
      code
      loungeName
      loungeSlug
      isValid
    }
  }
`)

const RegisterMutation = graphql(`
  mutation Register(
    $handle: String!
    $displayName: String!
    $password: String!
    $inviteCode: String
  ) {
    register(
      handle: $handle
      displayName: $displayName
      password: $password
      inviteCode: $inviteCode
    ) {
      id
      handle
      displayName
      isInstanceAdmin
      avatarUrl
      createdAt
    }
  }
`)

const LoginMutation = graphql(`
  mutation Login($handle: String!, $password: String!) {
    login(handle: $handle, password: $password) {
      id
      handle
      displayName
      isInstanceAdmin
      avatarUrl
      createdAt
    }
  }
`)

const LogoutMutation = graphql(`
  mutation Logout {
    logout
  }
`)

const UpdateDisplayNameMutation = graphql(`
  mutation UpdateDisplayName($displayName: String!) {
    updateDisplayName(displayName: $displayName) {
      id
      handle
      displayName
      isInstanceAdmin
      avatarUrl
      createdAt
    }
  }
`)

const SetRegistrationPolicyMutation = graphql(`
  mutation SetRegistrationPolicy($policy: RegistrationPolicy!) {
    setRegistrationPolicy(policy: $policy) {
      registrationPolicy
      needsSetup
      voiceParticipantLimit
      videoPublisherLimit
      attachmentMaxBytes
      attachmentMaxPerMessage
      avatarMaxBytes
      cameraMaxHeight
      cameraMaxFps
      cameraMaxKbps
      screenMaxHeight
      screenMaxFps
      screenMaxKbps
      voiceAudioKbps
    }
  }
`)

/**
 * Reusable in three places without duplicating a key or a fetcher: components
 * (`useQuery`), router guards and loaders (`ensureQueryData`), and
 * prefetch-on-hover.
 */
export const meQueryOptions = () =>
  queryOptions({
    queryKey: authKeys.me(),
    queryFn: () => gqlClient.request(MeQuery),
    // The router guard reads this on every navigation. Trusting it briefly is
    // the difference between instant navigation and a request per click; the
    // server is still the authority on every mutation.
    staleTime: 30_000,
    // A signed-out visitor is a valid answer, not a failure to retry.
    retry: false,
  })

export const instanceQueryOptions = () =>
  queryOptions({
    queryKey: authKeys.instance(),
    queryFn: () => gqlClient.request(InstanceQuery),
    staleTime: 60_000,
  })

export const invitePreviewQueryOptions = (code: string) =>
  queryOptions({
    queryKey: authKeys.invitePreview(code),
    queryFn: () => gqlClient.request(InvitePreviewQuery, { code }),
    // A bad code is an answer, not something to retry.
    retry: false,
  })

export function useMe() {
  return useQuery(meQueryOptions())
}

export function useInstance() {
  return useQuery(instanceQueryOptions())
}

export function useInvitePreview(code: string) {
  return useQuery(invitePreviewQueryOptions(code))
}

export function useRegister() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      handle: string
      displayName: string
      password: string
      inviteCode?: string
    }) => gqlClient.request(RegisterMutation, input),
    onSuccess: async (data) => {
      // Seed rather than invalidate: the mutation already returned the user, so
      // refetching it would be a round trip for something we hold.
      client.setQueryData(authKeys.me(), { me: data.register })
      // needsSetup just changed for the first registration, and myLounges may
      // now hold an invited lounge.
      await client.invalidateQueries({ queryKey: authKeys.instance() })
      await client.invalidateQueries({ queryKey: ['lounges'] })
    },
  })
}

export function useLogin() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { handle: string; password: string }) =>
      gqlClient.request(LoginMutation, input),
    onSuccess: async (data) => {
      client.setQueryData(authKeys.me(), { me: data.login })
      await client.invalidateQueries({ queryKey: ['lounges'] })
    },
  })
}

export function useLogout() {
  return useMutation({
    mutationFn: () => gqlClient.request(LogoutMutation),
    onSuccess: () => {
      // Everything, not just `me`. Lounges, members and invites all belonged to
      // the person who just left, and the next person to sit down at this
      // browser must not see any of it.
      queryClient.clear()
    },
  })
}

export function useUpdateDisplayName() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (displayName: string) =>
      gqlClient.request(UpdateDisplayNameMutation, { displayName }),
    onSuccess: async (data) => {
      client.setQueryData(authKeys.me(), { me: data.updateDisplayName })
      // Member lists render display names.
      await client.invalidateQueries({ queryKey: ['lounges'] })
    },
  })
}

export function useSetRegistrationPolicy() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (policy: RegistrationPolicy) =>
      gqlClient.request(SetRegistrationPolicyMutation, { policy }),
    onSuccess: (data) => {
      client.setQueryData(authKeys.instance(), { instance: data.setRegistrationPolicy })
    },
  })
}

/**
 * Every number an admin may tune, and the one mutation that changes them.
 *
 * A separate query from `instance` because most of these are not public — how
 * many sign-ups an address gets per hour is exactly what somebody grinding this
 * instance would like to know. Only the three the client has to enforce itself
 * ride the public one.
 */
const InstanceLimitsQuery = graphql(`
  query InstanceLimits {
    instanceLimits {
      attachmentMaxMb
      attachmentMaxPerMessage
      avatarMaxMb
      messageBurstPerSecond
      messagePerMinute
      uploadPerHour
      invitesPerUserHourly
      liveInvitesPerLounge
      registerPerIpHourly
      registerPerIpDaily
      loginPerIp
      loginFailuresBeforeLock
      voiceParticipantLimit
      videoPublisherLimit
      cameraMaxHeight
      cameraMaxFps
      cameraMaxKbps
      screenMaxHeight
      screenMaxFps
      screenMaxKbps
      voiceAudioKbps
    }
  }
`)

const SetInstanceLimitsMutation = graphql(`
  mutation SetInstanceLimits($input: InstanceLimitsInput!) {
    setInstanceLimits(input: $input) {
      attachmentMaxMb
      attachmentMaxPerMessage
      avatarMaxMb
      messageBurstPerSecond
      messagePerMinute
      uploadPerHour
      invitesPerUserHourly
      liveInvitesPerLounge
      registerPerIpHourly
      registerPerIpDaily
      loginPerIp
      loginFailuresBeforeLock
      voiceParticipantLimit
      videoPublisherLimit
      cameraMaxHeight
      cameraMaxFps
      cameraMaxKbps
      screenMaxHeight
      screenMaxFps
      screenMaxKbps
      voiceAudioKbps
    }
  }
`)

export const instanceLimitsQueryOptions = () =>
  queryOptions({
    queryKey: authKeys.instanceLimits(),
    queryFn: () => gqlClient.request(InstanceLimitsQuery),
    staleTime: 60_000,
  })

export function useInstanceLimits() {
  return useQuery(instanceLimitsQueryOptions())
}

/**
 * Change some of them. One mutation for all twenty-one, every field optional.
 *
 * The server refuses anything outside each number's range with a VALIDATION
 * error naming the range, which the tab shows as written — the `min`/`max` on
 * the inputs is a courtesy, not the cap.
 *
 * `setQueryData` seeds the limits cache from the response, as every mutation
 * here does. The public `instance` query is *invalidated* rather than seeded,
 * because twelve of these numbers appear on it too and this response is not shaped
 * like that one. One refetch, on an action an admin takes rarely — and it is what
 * makes an admin raising a quality ceiling visible to everybody's device picker
 * without a reload.
 */
export function useSetInstanceLimits() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: InstanceLimitsInput) =>
      gqlClient.request(SetInstanceLimitsMutation, { input }),
    onSuccess: (data) => {
      client.setQueryData(authKeys.instanceLimits(), { instanceLimits: data.setInstanceLimits })
      void client.invalidateQueries({ queryKey: authKeys.instance() })
    },
  })
}

/**
 * Avatar upload — the one call that does not go through `gqlClient`.
 *
 * graphql-request v7 removed file-upload support, so bytes travel over a plain
 * multipart POST while GraphQL keeps the typed state (`me.avatarUrl`). Kept
 * here so no component ever sees the difference.
 */
export function useUploadAvatar() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)

      const response = await fetch('/avatars', {
        method: 'POST',
        credentials: 'include',
        // The same CSRF header the GraphQL client sends; the server requires it
        // on every non-GET.
        headers: { 'x-nook-client': 'web' },
        body,
      })

      if (!response.ok) {
        const problem = (await response.json().catch(() => null)) as { message?: string } | null
        throw new Error(problem?.message ?? 'That image could not be uploaded.')
      }

      return (await response.json()) as { version: number }
    },
    onSuccess: async () => {
      // The URL changed, so `me` has to be refetched — the version in it is
      // exactly what busts the browser's cache.
      await client.invalidateQueries({ queryKey: authKeys.me() })
      await client.invalidateQueries({ queryKey: ['lounges'] })
    },
  })
}

export function useRemoveAvatar() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await fetch('/avatars', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'x-nook-client': 'web' },
      })

      if (!response.ok) throw new Error('That avatar could not be removed.')
      return (await response.json()) as { version: number }
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: authKeys.me() })
      await client.invalidateQueries({ queryKey: ['lounges'] })
    },
  })
}


/**
 * Every account on the instance. Admin only — the server refuses anybody else.
 *
 * Keyed by the search term, so typing produces a new query rather than mutating
 * one, and TanStack keeps the previous list on screen while the next arrives.
 */
export const usersQueryOptions = (query: string) =>
  queryOptions({
    queryKey: authKeys.users(query),
    queryFn: () => gqlClient.request(UsersQuery, { query: query.trim() || null }),
    staleTime: 10_000,
  })

export function useUsers(query: string) {
  return useQuery(usersQueryOptions(query))
}

/** Switch an account off, or back on. */
export function useSetUserDisabled(query: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, disabled }: { userId: string; disabled: boolean }) =>
      gqlClient.request(SetUserDisabledMutation, { userId, disabled }),
    onSuccess: () => {
      // The whole list, not one row: disabling revokes sessions, and the next
      // read is the honest way to see what that did.
      void client.invalidateQueries({ queryKey: authKeys.users(query) })
    },
  })
}


/**
 * Choose a palette.
 *
 * Optimistic on the *document* rather than in the cache: the attribute is
 * repainted the instant you click, because a theme switch that waits for a
 * round trip is a theme switch that feels broken. The cache follows on success,
 * and a failure leaves the row of buttons showing what the server actually has.
 */
export function useSetThemePreference() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (preference: ThemePreference) =>
      gqlClient.request(SetThemePreferenceMutation, { preference }),
    onMutate: (preference) => {
      applyTheme(preference)
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: authKeys.me() })
    },
  })
}
